/**
 * WalletPage — F-009.3 (UC-010 steps 1-3 + ritorno da Checkout).
 *
 * Dashboard wallet del cliente business: saldo + fido + disponibilità
 * (WalletBalanceCard), banner di stato, storico movimenti paginato e modale di
 * ricarica (redirect a Stripe Checkout).
 *
 * Ritorno da Stripe: l'URL di success/cancel è `/app/wallet?ricarica=success|cancel`.
 * Al rientro mostriamo un toast e invalidiamo le query (il webhook accredita in
 * modo asincrono → un refetch poco dopo mostra il nuovo saldo), poi puliamo il
 * query param.
 *
 * Struttura (pattern admin-wallet): guard + layout + QueryClientProvider esterno,
 * `WalletPageInner` interno esportato per i test isolati.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RequireAuth } from '@/features/auth/components/RequireAuth';
import { AppLayout } from '@/components/layout/AppLayout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { walletQueryClient } from '../lib/query-client';
import { errorMessageFor } from '../lib/wallet-client';
import { useWalletSummary } from '../hooks/useWalletSummary';
import { useWalletTransactions } from '../hooks/useWalletTransactions';
import { useRicarica } from '../hooks/useRicarica';
import { useServiziCatalog, useAcquistaServizio } from '../hooks/useServiziCatalog';
import { WalletBalanceCard } from '../components/WalletBalanceCard';
import { RicaricaModal } from '../components/RicaricaModal';
import { TransactionsTable } from '../components/TransactionsTable';
import { ServiziCatalog } from '../components/ServiziCatalog';

const PAGE_LIMIT = 20;

export function WalletPage() {
  return (
    <RequireAuth roles={['CLIENT']}>
      <AppLayout>
        <QueryClientProvider client={walletQueryClient}>
          <WalletPageInner />
        </QueryClientProvider>
      </AppLayout>
    </RequireAuth>
  );
}

export function WalletPageInner() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);

  const summaryQuery = useWalletSummary();
  const txQuery = useWalletTransactions({ page, limit: PAGE_LIMIT });
  const ricarica = useRicarica();
  const catalogQuery = useServiziCatalog();
  const acquista = useAcquistaServizio();

  // Ritorno da Stripe Checkout: toast + invalidazione + pulizia query param.
  useEffect(() => {
    const esito = searchParams.get('ricarica');
    if (!esito) return;
    if (esito === 'success') {
      toast.success('Pagamento ricevuto. Il saldo sarà aggiornato a breve.');
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
    } else if (esito === 'cancel') {
      toast.info('Ricarica annullata.');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('ricarica');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, queryClient]);

  const items = txQuery.data?.items ?? [];
  const total = txQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight">Wallet</h1>
        <p className="text-muted-foreground">
          Ricarica il saldo e consulta i movimenti del tuo wallet prepagato.
        </p>
      </header>

      {summaryQuery.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>Errore</AlertTitle>
          <AlertDescription>{errorMessageFor(summaryQuery.error)}</AlertDescription>
        </Alert>
      ) : null}

      {summaryQuery.isLoading ? (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Caricamento wallet…
        </p>
      ) : null}

      {summaryQuery.data ? (
        <WalletBalanceCard summary={summaryQuery.data} onRicarica={() => setModalOpen(true)} />
      ) : null}

      {(catalogQuery.data?.items.length ?? 0) > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Servizi disponibili</CardTitle>
          </CardHeader>
          <CardContent>
            <ServiziCatalog
              items={catalogQuery.data?.items ?? []}
              onAcquista={(id) => acquista.mutate(id)}
              pendingId={acquista.isPending ? (acquista.variables ?? null) : null}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Movimenti</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {txQuery.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertTitle>Errore</AlertTitle>
              <AlertDescription>{errorMessageFor(txQuery.error)}</AlertDescription>
            </Alert>
          ) : null}

          {txQuery.isLoading ? (
            <p className="text-sm text-muted-foreground" aria-live="polite">
              Caricamento movimenti…
            </p>
          ) : null}

          {!txQuery.isLoading && !txQuery.isError && items.length === 0 ? (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              Nessun movimento ancora. Effettua la tua prima ricarica.
            </div>
          ) : null}

          {items.length > 0 ? (
            <>
              <TransactionsTable items={items} />
              {totalPages > 1 ? (
                <div className="flex items-center justify-between pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    Precedente
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Pagina {page} di {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Successiva
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </CardContent>
      </Card>

      <RicaricaModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onConfirm={(amountCents) => ricarica.mutate(amountCents)}
        isPending={ricarica.isPending}
      />
    </section>
  );
}
